import * as Popover from '@radix-ui/react-popover';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    GANTT_GROUP_BY,
    GANTT_TIME_SCALES,
    type GanttGroupBy,
    type GanttTimeScale,
    type NormalisedGanttConfig,
} from './gantt-config';

// ============================================
// GANTT SETTINGS PANEL
// ============================================
//
// t17: popover with three controls — time scale (Day / Week / Month /
// Auto), group-by (status / assignee / none), and showSprints
// (boolean toggle). Each change writes back to the saved view's
// config via onConfigChange so the sidebar / other Gantt mounts
// reflect the change.
//
// R7 / t33: the popover is now an *uncontrolled* child of the
// <Popover.Root> in GanttView. The toolbar's cog button is the
// <Popover.Trigger asChild>, so open-state and anchor are owned by
// Radix internally — passing `anchor={anchorRef.current}` as a
// Content prop was silently ignored because Radix Popper reads its
// reference element from the PopperContext (set by Trigger /
// Anchor), not from a Content prop.

export interface GanttSettingsPanelProps {
    config: NormalisedGanttConfig;
    /** True if the project has at least one Sprint custom field. */
    hasSprintField: boolean;
    onConfigChange: (next: NormalisedGanttConfig) => void;
}

export function GanttSettingsPanel({
    config,
    hasSprintField,
    onConfigChange,
}: GanttSettingsPanelProps) {
    const { t } = useI18n();

    return (
        <Popover.Portal>
            <Popover.Content
                align="end"
                sideOffset={6}
                className="z-50 w-80 rounded-md border border-border bg-surface p-4 shadow-lg space-y-4"
                data-testid="gantt-settings-panel"
            >
                    <div>
                        <h4 className="text-sm font-medium text-foreground mb-1">
                            {t('gantt.settingsTitle')}
                        </h4>
                        <p className="text-xs text-foreground-muted">
                            {t('gantt.settingsHint')}
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="gantt-timeScale" className="text-xs font-medium">
                            {t('gantt.timeScaleLabel')}
                        </Label>
                        <Select
                            value={config.timeScale}
                            onValueChange={(v) =>
                                onConfigChange({ ...config, timeScale: v as GanttTimeScale })
                            }
                        >
                            <SelectTrigger id="gantt-timeScale" className="h-8">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {GANTT_TIME_SCALES.map((scale) => (
                                    <SelectItem key={scale} value={scale}>
                                        {t(`gantt.timeScale.${scale}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="gantt-groupBy" className="text-xs font-medium">
                            {t('gantt.groupByLabel')}
                        </Label>
                        <Select
                            value={config.groupBy}
                            onValueChange={(v) =>
                                onConfigChange({ ...config, groupBy: v as GanttGroupBy })
                            }
                        >
                            <SelectTrigger id="gantt-groupBy" className="h-8">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {GANTT_GROUP_BY.map((g) => (
                                    <SelectItem key={g} value={g}>
                                        {t(`gantt.groupBy.${g}`)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label
                            htmlFor="gantt-showSprints"
                            className="text-xs font-medium flex items-center gap-2"
                        >
                            <input
                                id="gantt-showSprints"
                                type="checkbox"
                                className="h-4 w-4 rounded border-white/20 bg-white/5 accent-primary cursor-pointer"
                                checked={config.showSprints}
                                disabled={!hasSprintField}
                                onChange={(e) =>
                                    onConfigChange({
                                        ...config,
                                        showSprints: e.target.checked,
                                    })
                                }
                            />
                            {t('gantt.showSprintsLabel')}
                        </Label>
                        <p className="text-xs text-foreground-muted">
                            {!hasSprintField
                                ? t('gantt.requiresSprint')
                                : t('gantt.showSprintsHint')}
                        </p>
                    </div>

                    {/* R3 / t17: show tasks without dates. R1/R2 pinned
                        them at barLeft=0 which cluttered the visible
                        bars. Default OFF so dateless tasks stay
                        hidden; flipping this toggle on brings them
                        back at the bottom of their group. */}
                    <div className="space-y-2">
                        <Label
                            htmlFor="gantt-showDateless"
                            className="text-xs font-medium flex items-center gap-2"
                        >
                            <input
                                id="gantt-showDateless"
                                type="checkbox"
                                className="h-4 w-4 rounded border-white/20 bg-white/5 accent-primary cursor-pointer"
                                checked={config.showDateless}
                                onChange={(e) =>
                                    onConfigChange({
                                        ...config,
                                        showDateless: e.target.checked,
                                    })
                                }
                            />
                            {t('gantt.showDatelessLabel')}
                        </Label>
                        <p className="text-xs text-foreground-muted">
                            {t('gantt.showDatelessHint')}
                        </p>
                    </div>

                    <div className="pt-2 flex justify-end">
                        <Popover.Close asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                            >
                                {t('common.close')}
                            </Button>
                        </Popover.Close>
                    </div>
                </Popover.Content>
            </Popover.Portal>
    );
}
